import { useState } from 'react';
import { LanguageCode } from 'i18n/internationalization.types';
import useSystemTemplatePreviewMutation from 'components/domain/automation/systemTemplates/useSystemTemplatePreviewMutation';
import RichTextEditorField from 'components/forms/richTextEditor/RichTextEditorField';
import SelectField from 'components/forms/selectField/SelectField';
import TextField from 'components/forms/textField/TextField';
import { MessagingType } from 'models/automation/CustomTemplates';
import { UsageType } from 'models/automation/Common';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TemplateModalLanguageSelectField from 'pages/automations/common/TemplateModalLanguageSelectField';
import useCustomTemplateToolbarSettings from './useCustomTemplateToolbarSettings';

const CustomTemplateAddEditModalFormFields = ({
  availableLanguages,
}: {
  availableLanguages: LanguageCode[];
}) => {
  const { t } = useTranslation();
  const messagingTypeValue = useWatch({ name: 'messagingType' });
  const languageValue = useWatch({ name: 'language' });
  const [isOnPreviewMode, setIsOnPreviewMode] = useState(false);
  const {
    data: previewData,
    mutateAsync: fetchPreview,
    isLoading: isLoadingPreview,
  } = useSystemTemplatePreviewMutation();

  const shouldShowSubject = messagingTypeValue === MessagingType.EMAIL;

  const { toolbarSettings, isLoadingVariables } =
    useCustomTemplateToolbarSettings({
      loadPreview: async (content) => {
        const { data } = await fetchPreview({ htmlContent: content });

        return data.previewContent;
      },
      messagingType: messagingTypeValue,
      onEnterPreviewMode: () => setIsOnPreviewMode(true),
      onExitPreviewMode: () => setIsOnPreviewMode(false),
    });

  return (
    <div key={languageValue}>
      <SelectField
        name="messagingType"
        colSmInput={9}
        colSmLabel={3}
        label={t('pageAutomations.customTemplates.modal.templateType')}
        options={Object.values(MessagingType).map((type) => ({
          label: t('pageAutomations.customTemplates.types.messagingType', {
            context: type,
          }),
          value: type,
        }))}
        disabled
        required
      />

      <TextField
        required
        name="name"
        colSmInput={9}
        colSmLabel={3}
        label={t('pageAutomations.customTemplates.modal.templateName')}
      />

      <TemplateModalLanguageSelectField usageType={UsageType.CUSTOM} />

      {shouldShowSubject && isOnPreviewMode && (
        <TextField
          colSmInput={9}
          colSmLabel={3}
          disabled
          isLoading={isLoadingPreview}
          key={`subject-${languageValue}`}
          label={t('pageAutomations.customTemplates.modal.subject')}
          name="subjectPreview"
          required
          value={
            previewData?.data.previewTitle ??
            t('form.editor.preview.errorMessage')
          }
        />
      )}

      {availableLanguages.map(
        (language) =>
          language &&
          languageValue === language && (
            <>
              {shouldShowSubject && !isOnPreviewMode && (
                <TextField
                  required
                  key={`subject-${languageValue}`}
                  name={`locales.${languageValue}.subject`}
                  colSmInput={9}
                  colSmLabel={3}
                  label={t('pageAutomations.customTemplates.modal.subject')}
                />
              )}

              <RichTextEditorField
                colSmInput={12}
                key={`content-${languageValue}`}
                linkMode="edit"
                name={`locales.${languageValue}.content`}
                toolbarSettings={toolbarSettings}
                isLoading={isLoadingVariables}
              />
            </>
          ),
      )}
    </div>
  );
};

export default CustomTemplateAddEditModalFormFields;
